import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUtensils, FaSearch, FaTimes } from "react-icons/fa";
import Product from "../components/Product";
import MenuSkeleton from "../components/MenuSkeleton";
import Message from "../components/Message";
import EmptyState from "../components/EmptyState";
import CartSummarySidebar from "../components/CartSummarySidebar";
import { t } from "../i18n/translations";
import "./ProductScreen.css";
import { listProducts } from "../actions/productActions";

// Synthetic category key for the "popular" section — deliberately not a
// real category string, so it can never collide with a restaurant's own
// category name (which come straight from free-text product.category).
const POPULAR_CATEGORY_KEY = "__popular__";

// Roughly the sticky category bar's own rendered height — used to
// offset scroll-to-category so the sticky bar doesn't cover the target
// section's top edge once it's engaged.
const CATEGORY_BAR_STICKY_HEIGHT = 56;

function ProductScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const productList = useSelector((state) => state.productList);
  const { error, loading, products = [] } = productList;

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const { language } = useSelector((state) => state.language);
  const { restaurant = {} } = useSelector((state) => state.restaurantDetails || {});
  const isClosed = restaurant.is_open_now === false;
  const hidePrices = restaurant.hide_prices === true;

  // Optional grouping one level above categories (e.g. Étlap/Itallap/
  // Borlap). Most restaurants never create one — `menus` stays empty and
  // this whole switcher is skipped, leaving the flat single-menu layout
  // exactly as it was.
  const menus = restaurant.menus || [];
  const categoryMenus = restaurant.category_menus || {};
  const [activeMenu, setActiveMenu] = useState(null);

  // A category with no explicit menu assignment falls back to the first
  // menu (by sort_order) rather than disappearing.
  const resolveMenuId = (category) => {
    if (menus.length === 0) return null;
    const explicit = categoryMenus[category];
    return explicit != null ? explicit : menus[0].id;
  };

  const activeMenuObj =
    menus.length > 1 ? menus.find((m) => m.id === activeMenu) : null;

  // Menu names are free text set by the restaurant, so it isn't safe to
  // grammatically inflect them (Hungarian case endings depend on vowel
  // harmony) — "Keresés – Itallap..." sidesteps that instead of guessing.
  const searchPlaceholder = activeMenuObj
    ? `${t("searchPrefix", language)} – ${activeMenuObj.name}...`
    : t("searchPlaceholder", language);

  // Default to the first menu once menus load — guests pick one lap at a
  // time (Étlap, then Itallap, ...) instead of landing on one giant
  // combined list, which is the whole point of this feature.
  useEffect(() => {
    if (menus.length > 1 && activeMenu == null) {
      setActiveMenu(menus[0].id);
    }
    if (menus.length <= 1 && activeMenu != null) {
      setActiveMenu(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus.length]);

  const [activeCategory, setActiveCategory] = useState("");
  const [categorySticky, setCategorySticky] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const categoryBarRef = useRef(null);
  const categoryRefs = useRef({});
  // Zero-height marker rendered as this component's first element — it
  // sits immediately after RestaurantHero in normal document flow (the
  // hero itself scrolls away with the page, it doesn't stick). Its
  // scroll position is what decides when the category bar should go
  // sticky, decoupling this screen from the hero's internal implementation.
  const heroSentinelRef = useRef(null);

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const table = query.get("table");

  /* ===== BODY CLASS ===== */
  useEffect(() => {
    document.body.classList.add("product-page");
    return () => document.body.classList.remove("product-page");
  }, []);

  /* ===== LOAD PRODUCTS (SUBDOMAIN ALAPÚ) — re-fetch whenever the
     selected language changes so translated content is loaded ===== */
  useEffect(() => {
    dispatch(listProducts());
  }, [dispatch, language]);

  const visibleProducts = products.filter((p) => p.visible);

  /* ===== GROUP PRODUCTS =====
     Memoized so `categories` keeps a stable reference across renders —
     it's a dependency of the effects below, and "Népszerű" (a synthetic
     section pulled from every category's popular=true items, pinned to
     the top, shown only when at least one product is popular) needs the
     same grouping pass as the real categories. */
  const { groupedProducts, categories } = useMemo(() => {
    const visible = products.filter((p) => p.visible);
    const inMenu =
      menus.length > 1 && activeMenu != null
        ? visible.filter((p) => resolveMenuId(p.category) === activeMenu)
        : visible;
    const trimmedQuery = searchQuery.trim();
    const searched = trimmedQuery
      ? inMenu.filter((p) =>
          p.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
        )
      : inMenu;

    const grouped = searched.reduce((groups, product) => {
      if (!groups[product.category]) groups[product.category] = [];
      groups[product.category].push(product);
      return groups;
    }, {});

    const popular = searched.filter((p) => p.popular);
    if (popular.length > 0) {
      grouped[POPULAR_CATEGORY_KEY] = popular;
    }

    const cats = popular.length > 0
      ? [POPULAR_CATEGORY_KEY, ...Object.keys(grouped).filter((c) => c !== POPULAR_CATEGORY_KEY)]
      : Object.keys(grouped);

    return { groupedProducts: grouped, categories: cats };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchQuery, activeMenu, menus.length]);

  const getCategoryLabel = (category) =>
    category === POPULAR_CATEGORY_KEY ? t("popularCategoryLabel", language) : category;

  // Covers both "no search hits" and "this menu (Étlap/Itallap/...) has no
  // visible products yet" — same empty layout, different copy.
  const isSearching = searchQuery.trim() !== "";
  const noSearchResults = categories.length === 0;

  /* ===== INITIAL / RESET ACTIVE CATEGORY =====
     Re-picks the first category whenever the current one no longer exists
     in the active set — covers first load, search, and switching menus
     (Étlap → Itallap etc.), all of which can change `categories` out from
     under whatever was previously selected. */
  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  /* ===== SCROLL LOGIC ===== */
  useEffect(() => {
    const handleScroll = () => {
      const sentinel = heroSentinelRef.current;
      if (sentinel) {
        setCategorySticky(sentinel.getBoundingClientRect().top <= 0);
      }

      const trigger = window.innerHeight / 3;
      let newActive = activeCategory;

      for (let category of categories) {
        const el = document.getElementById(`cat-${category}`);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        if (rect.top <= trigger && rect.bottom > trigger) {
          newActive = category;
          break;
        }
      }

      if (newActive !== activeCategory) {
        setActiveCategory(newActive);

        const btn = categoryRefs.current[newActive];
        const container = categoryBarRef.current;
        if (btn && container) {
          const btnRect = btn.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const targetLeft =
            container.scrollLeft + (btnRect.left - containerRect.left) - 24;

          container.scrollTo({
            left: targetLeft,
            behavior: "smooth",
          });
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [categories, activeCategory]);

  /* ===== STATES ===== */
  if (loading) {
    return (
      <div className="home-container">
        <MenuSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <Message variant="danger">{error}</Message>
      </div>
    );
  }

  if (visibleProducts.length === 0) {
    return (
      <div className="home-container">
        <EmptyState
          icon={<FaUtensils />}
          title={t("emptyMenuTitle", language)}
          message={t("emptyMenuMessage", language)}
        />
      </div>
    );
  }

  return (
    <div className="home-container">
      <div ref={heroSentinelRef} aria-hidden="true" />

      {isClosed && (
        <Message variant="danger">{t("closedOrderingMessage", language)}</Message>
      )}

      {/* MENU VÁLTÓ (Étlap / Itallap / Borlap stb.) — csak ha az étterem
          több menüt is beállított, egyébként ugyanaz a lapos lista, mint
          eddig. */}
      {menus.length > 1 && (
        <div className="menu-switcher">
          {menus.map((menu) => (
            <button
              key={menu.id}
              type="button"
              className={`menu-switcher-item ${activeMenu === menu.id ? "active" : ""}`}
              onClick={() => setActiveMenu(menu.id)}
            >
              {menu.name}
            </button>
          ))}
        </div>
      )}

      {/* SEARCH */}
      <div className="menu-search">
        <FaSearch className="menu-search-icon" />
        <input
          type="text"
          className="menu-search-input"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            className="menu-search-clear"
            onClick={() => setSearchQuery("")}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {/* CATEGORY BAR */}
      {!noSearchResults && (
        <div className={`category-bar ${categorySticky ? "sticky" : ""}`}>
          <div ref={categoryBarRef} className="category-bar-inner">
            {categories.map((category) => (
              <span
                key={category}
                ref={(el) => (categoryRefs.current[category] = el)}
                className={`category-item ${
                  activeCategory === category ? "active" : ""
                }`}
                onClick={() => {
                  const el = document.getElementById(`cat-${category}`);
                  if (!el) return;

                  const offset = categorySticky ? CATEGORY_BAR_STICKY_HEIGHT : 0;
                  const y = el.getBoundingClientRect().top + window.pageYOffset;

                  window.scrollTo({
                    top: y - offset,
                    behavior: "smooth",
                  });
                }}
              >
                {getCategoryLabel(category)}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="menu-layout">
        <div className="menu-layout-content">
          {noSearchResults ? (
            isSearching ? (
              <EmptyState
                icon={<FaSearch />}
                title={t("noProductsFoundTitle", language)}
                message={t("noProductsFoundMessage", language)}
              />
            ) : (
              <EmptyState
                icon={<FaUtensils />}
                title={t("emptyMenuTitle", language)}
                message={t("emptyMenuMessage", language)}
              />
            )
          ) : (
            <div
              className={`menu-content ${categorySticky ? "with-sticky" : ""}`}
            >
              {categories.map((category) => (
                <div
                  key={category}
                  id={`cat-${category}`}
                  className="category-wrapper"
                >
                  <h3 className="category-title">{getCategoryLabel(category)}</h3>

                  <div className="category-section">
                    {groupedProducts[category].map((product) => (
                      <Product
                        key={product._id}
                        product={product}
                        cartItems={cartItems}
                        table={table}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CART SIDEBAR — desktop only, mirrors the mobile cart-fab below */}
        <CartSummarySidebar table={table} />
      </div>

      {/* CART BUTTON — mobile/tablet only, hidden on desktop where the
          sidebar above takes over (see ProductScreen.css breakpoints) */}
      {cartItems.length > 0 && (
        <div
          className="cart-fab"
          onClick={() => navigate(table ? `/cart?table=${table}` : "/cart")}
        >
          <div className="cart-fab-count">
            {cartItems.reduce((acc, item) => acc + item.qty, 0)}
          </div>
          <div className="cart-fab-text">{t("viewCart", language)}</div>
          {!hidePrices && (
            <div className="cart-fab-price">
              {cartItems.reduce((acc, item) => acc + item.price * item.qty, 0)} Ft
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductScreen;
